import React, { useState, useEffect } from "react";
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useApp } from "../App";
import { useNavigate } from "react-router-dom";
import KeptiIcon from "../components/KeptiIcon";
import { IconEmptyBox, IconEmptyCheck, IconEmptyHeart, IconHeart, IconNoListing, IconFlag } from "../components/Icons";

export default function MyListings() {
  const { t, user, setShowPost } = useApp();
  const [listings, setListings] = useState([]);
  const [saved, setSaved] = useState([]);
  const [tab, setTab] = useState("active");
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "listings"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setListings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user || tab !== "saved") return;
    getDoc(doc(db, "users", user.uid)).then(d => {
      if (!d.exists()) return;
      const savedIds = d.data().saved || [];
      if (savedIds.length === 0) { setSaved([]); return; }
      Promise.all(
        savedIds.map(id =>
          getDoc(doc(db, "listings", id)).then(ld =>
            ld.exists() ? { id: ld.id, ...ld.data() } : null
          )
        )
      ).then(results => setSaved(results.filter(Boolean)));
    });
  }, [user, tab]);

  const filtered = listings.filter(l => l.status === tab);
  const displayListings = tab === "saved" ? saved : filtered;

  const conditionLabel = (c) => {
    if (c === "likeNew") return t.likeNew;
    if (c === "good") return t.good;
    return t.used;
  };

  const conditionClass = (c) => {
    if (c === "likeNew") return "badge-new";
    if (c === "good") return "badge-good";
    return "badge-used";
  };

  const emptyStates = {
    active: { icon: <IconEmptyBox />, text: t.noListings, showSell: true },
    sold: { icon: <IconEmptyCheck />, text: "No sold items yet.", showSell: false },
    saved: { icon: <IconEmptyHeart />, text: "No saved listings yet. Tap the heart on any listing to save it.", showSell: false },
  };

  return (
    <>
      <div className="topbar">
        <div className="topbar-row">
          <div className="topbar-logo">
            <KeptiIcon size={28} />
            <span className="brand-wordmark">kepti</span>
          </div>
          <button className="icon-btn" onClick={() => setShowPost(true)} style={{ fontSize: 24 }}>+</button>
        </div>
      </div>

      <div className="filter-strip">
        <div className={`chip ${tab === "active" ? "active" : ""}`} onClick={() => setTab("active")}>{t.active}</div>
        <div className={`chip ${tab === "sold" ? "active" : ""}`} onClick={() => setTab("sold")}>{t.sold}</div>
        <div className={`chip ${tab === "saved" ? "active" : ""}`} onClick={() => setTab("saved")} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <IconHeart filled={tab === "saved"} /> Saved
        </div>
      </div>

      {displayListings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{emptyStates[tab].icon}</div>
          <p className="empty-text">{emptyStates[tab].text}</p>
          {emptyStates[tab].showSell && (
            <button className="btn-primary" onClick={() => setShowPost(true)} style={{ marginTop: 20, maxWidth: 200 }}>
              {t.sell}
            </button>
          )}
        </div>
      ) : (
        <div className="listings-grid" style={{ padding: "16px 16px 100px" }}>
          {displayListings.map(listing => (
            <div key={listing.id} className="listing-card" onClick={() => navigate(`/listing/${listing.id}`)}>
              <div className="listing-img">
                {listing.imageUrl ? <img src={listing.imageUrl} alt={listing.itemName} /> : <IconNoListing />}
                {listing.status === "sold" && <div className="badge-sold">SOLD</div>}
                {listing.status === "active" && (
                  <span className={`condition-badge ${conditionClass(listing.condition)}`}>
                    {conditionLabel(listing.condition)}
                  </span>
                )}
              </div>
              <div className="listing-body">
                <div className="listing-name">{listing.itemName}</div>
                <div className="listing-meta">{listing.size} · {t[listing.category] || listing.category}</div>
                <div className="listing-footer">
                  <span className="listing-price">€{listing.price}</span>
                  {listing.flagged && <IconFlag />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
